import React, { useCallback, useEffect, useState } from "react";
import { Alert, Card, CardBody, Col, Container, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { reload, sendEmailVerification } from "firebase/auth";

import logodark from "../../assets/images/logotop.png";
import { auth } from "../../firebase";
import { authErrorMessage, isGmail } from "../../helpers/authErrors";
import {
  markVerificationSent,
  needsEmailVerification,
  sendVerification,
  verificationSentAt,
} from "../../helpers/emailVerification";

// Firebase corta los pedidos seguidos (auth/too-many-requests).
const RESEND_WAIT = 60 * 1000;

// Pantalla que ve una cuenta nueva con email y clave hasta que confirma su
// email. `onVerified` avisa que ya puede pasar.
const VerifyEmail = ({ email, onVerified }) => {
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const address = auth.currentUser?.email || email;

  const send = useCallback(async (manual) => {
    const user = auth.currentUser;
    if (!user) return;
    if (manual && Date.now() - verificationSentAt(user.uid) < RESEND_WAIT) {
      setNotice({ color: "info", text: "Acabamos de enviártelo. Espera un minuto antes de pedir otro." });
      return;
    }
    setBusy(true);
    try {
      await sendVerification((settings) => sendEmailVerification(user, settings));
      markVerificationSent(user.uid);
      if (manual) setNotice({ color: "success", text: "Listo, te lo enviamos de nuevo." });
    } catch (error) {
      console.error("[auth]", error?.code, error?.message);
      setNotice({ color: "danger", text: authErrorMessage(error, "reset") });
    }
    setBusy(false);
  }, []);

  const check = useCallback(async (manual) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await reload(user);
    } catch (error) {
      console.error("[auth]", error?.code, error?.message);
      if (manual) setNotice({ color: "danger", text: authErrorMessage(error) });
      return;
    }
    if (!needsEmailVerification(user)) {
      // El token guardado todavía dice "sin confirmar": se renueva.
      try { await user.getIdToken(true); } catch (e) { }
      onVerified();
    } else if (manual) {
      setNotice({
        color: "warning",
        text: "Todavía no vemos la confirmación. Abre el email que te enviamos y toca el enlace.",
      });
    }
  }, [onVerified]);

  // Si la cuenta llegó hasta acá sin que se le haya enviado el email (falló
  // al registrarse, o entró desde otro navegador), se le envía ahora.
  useEffect(() => {
    const user = auth.currentUser;
    if (user && !verificationSentAt(user.uid)) send(false);
  }, [send]);

  // Lo normal es confirmar en otra pestaña y volver: al volver se revisa solo.
  useEffect(() => {
    const onFocus = () => check(false);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [check]);

  useEffect(() => {
    document.body.className = "bg-pattern";
    return function cleanup() {
      document.body.className = "";
    };
  });

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card className="shadow" style={{ borderRadius: 12, border: "none" }}>
                <CardBody className="p-4">
                  <div className="text-center mb-4">
                    <img src={logodark} alt="" height="50" className="auth-logo logo-dark mx-auto" />
                    <h4 className="mt-3 mb-1" style={{ fontWeight: 700, color: "#333" }}>
                      Confirma tu email
                    </h4>
                    <p className="text-muted" style={{ fontSize: 14 }}>Es un solo paso y se hace una única vez</p>
                  </div>

                  {notice ? (
                    <Alert color={notice.color} style={{ borderRadius: 8, fontSize: 14 }}>
                      {notice.text}
                    </Alert>
                  ) : null}

                  <div
                    style={{ background: "#f5f7fa", border: "1px solid #e3e8ef", borderRadius: 10, padding: 16, fontSize: 14, color: "#333" }}
                  >
                    <div className="d-flex align-items-start">
                      <span className="material-symbols-rounded me-2" style={{ fontSize: 22, color: "#1a73e8" }}>
                        mark_email_unread
                      </span>
                      <div>
                        Te enviamos un email a <strong style={{ wordBreak: "break-all" }}>{address}</strong>.
                        Ábrelo y toca el enlace para confirmar que es tuyo. Después vuelve a esta pantalla.
                      </div>
                    </div>
                  </div>
                  <p className="text-muted mt-2 mb-4" style={{ fontSize: 12 }}>
                    Si no lo ves en unos minutos, revisa la carpeta de spam.
                  </p>

                  <div className="d-grid">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => check(true)}
                      style={{ borderRadius: 8, padding: "10px", fontWeight: 600, fontSize: 15 }}
                    >
                      <span className="material-symbols-rounded me-1" style={{ fontSize: 18, verticalAlign: "middle" }}>check_circle</span>
                      Ya lo confirmé
                    </button>
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link text-muted p-0"
                      style={{ fontSize: 13 }}
                      disabled={busy}
                      onClick={() => send(true)}
                    >
                      {busy ? "Enviando…" : "No me llegó: enviar el email otra vez"}
                    </button>
                  </div>

                  <hr />
                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    ¿El email está mal escrito? Toca «Salir» y crea la cuenta de nuevo con el correcto.
                    {isGmail(address)
                      ? " Como es de Gmail, también puedes salir y entrar con «Continuar con Google», sin confirmar nada."
                      : ""}
                  </p>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <Link to="/logout" className="fw-medium text-primary" style={{ fontSize: 14 }}>
                  Salir
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VerifyEmail;
